import React, { useEffect, useState } from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Tooltip from '@mui/material/Tooltip';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import FileUpload from 'components/Inputs/FileUpload';
import Loader from 'components/Loader/Loader';
import { parseCSV } from 'helpers/parseFile';
import { API_URL } from '../../configs/Api';
import { getHeaders } from '../../middleware/Api';
const styles = require('./styles.pcss');

type Props = {
  enqueueSnackBarMessage: Function;
  organizationId: string;
};

export default function BulkCategoryUploadTool(props: Props) {
  const [disableExistingCategories, setDisableExistingCategories] =
    useState(false);
  const [allowDuplicateExternalIds, setAllowMultipleCategories] = useState(true);
  const [disableForOtherPolicies, setDisableForOtherPolicies] = useState(false);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleUpload = async () => {
    if (!csvFile) {
      props.enqueueSnackBarMessage('Must upload CSV file.', 'error');
      return;
    }
    setIsLoading(true);
    try {
      const parsedData = await parseCSV(csvFile);
      if (parsedData.length <= 0) throw new Error('No data to upload');
      else {
        const columns = Object.keys(parsedData[0]);
        const whiteList = ['title', 'externalAccountId'];
        if (columns.some((col) => !whiteList.includes(col)))
          throw new Error(
            'incorrect csv headers, can only have "title" and "externalAccountId"',
          );
      }
      const res = await (
        await fetch(API_URL + 'job/import-expense-categories', {
          method: 'post',
          headers: getHeaders(),
          body: JSON.stringify({
            organizationId: props.organizationId,
            disableExistingCategories,
            disableForOtherPolicies,
            allowDuplicateExternalIds,
            expenseCategories: parsedData,
          }),
        })
      ).json();
      if (res.type === 'data') {
        props.enqueueSnackBarMessage(
          'Your categories are being processed, please check the app in a minute to see if they were successfully uploaded.',
          'success',
        );
      }
    } catch (e: any) {
      props.enqueueSnackBarMessage('Error: ' + e.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileChange = (files: FileList) => {
    if (files[0]) {
      setCsvFile(files[0]);
    }
  };

  return (
    <Card className={styles.bulkCategoryUploadCard}>
      <CardHeader
        title="Bulk Category Upload Tool"
        className={styles.header}
      />
      <CardContent>
        <Typography
          variant="body1"
          className={styles.description}
        >
          Use this tool to upload categories in bulk. Click the file upload area
          below to begin the process.
        </Typography>

        <FileUpload
          onChange={handleFileChange}
          value={csvFile}
          mimeType="text/csv"
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={disableExistingCategories}
              onChange={(e) => setDisableExistingCategories(e.target.checked)}
              color="primary"
            />
          }
          label="Disable Existing Categories"
        />

        <div className={styles.checkboxWithTooltip}>
          <FormControlLabel
            control={
              <Checkbox
                checked={allowDuplicateExternalIds}
                onChange={(e) => setAllowMultipleCategories(e.target.checked)}
                color="primary"
              />
            }
            label="Allow Multiple Categories to Link to Same GL Account"
          />
          <Tooltip
            title="This allows the creation of multiple categories with different names that will link to the same account in the client's general ledger. Use Case: Breaking categories into subcategories within TB, but want to sync to the same GL account."
            arrow
          >
            <IconButton
              size="small"
              className={styles.tooltipIcon}
            >
              <HelpOutlineIcon />
            </IconButton>
          </Tooltip>
        </div>

        <div className={styles.buttonContainer}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleUpload}
            className={styles.button}
            disabled={isLoading}
          >
            Upload Categories
          </Button>
        </div>
      </CardContent>
      {isLoading && (
        <div className={styles.infoLoader}>
          <Loader visible={true} />
        </div>
      )}
    </Card>
  );
}
